import * as errors from '@attain-apps/errors'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

/**
 * How an endpoint exposes records beyond its first response.
 *
 * Third-party APIs do not agree on a pagination scheme, and a single provider
 * often uses more than one across its endpoints. What they have in common is a
 * value that continues pagination, so a source declares where that value is
 * read from and which query parameter carries it back. Describing the
 * mechanism rather than the provider keeps supporting a new third-party system
 * a matter of writing a definition rather than adding code.
 *
 * Every request is built from the source's own path, query and page size, so
 * nothing an API returns is ever requested as a URL in its own right. That
 * keeps one way of addressing every endpoint, and keeps the connector's API
 * key from being sent anywhere the connector's base URL does not point.
 *
 * @example Jira `/rest/api/3/search/jql`, a token read from the body.
 * ```ts
 * {
 *   from: { type: 'body', path: 'nextPageToken' },
 *   parameter: 'nextPageToken',
 *   size: { parameter: 'maxResults', value: 100 },
 * }
 * ```
 * @example GitHub `/pulls`, page numbers counted locally, because the response
 * is a bare array that reports nothing about the pages around it.
 * ```ts
 * {
 *   from: { type: 'count', unit: 'pages' },
 *   parameter: 'page',
 *   size: { parameter: 'per_page', value: 100 },
 * }
 * ```
 * @example Jira `/rest/api/3/project/search`, a record offset counted locally
 * and stopped by the total the response reports.
 * ```ts
 * {
 *   from: { type: 'count', unit: 'records', totalPath: 'total' },
 *   parameter: 'startAt',
 *   size: { parameter: 'maxResults', value: 100 },
 * }
 * ```
 */
export interface Pagination {
  /** @see {@link Pagination.From} */
  from: Pagination.From
  /**
   * Query parameter carrying the continuation value on the next request.
   *
   * @example 'nextPageToken'
   * @example 'startAt'
   * @example 'page'
   */
  parameter: string
  /** @see {@link Pagination.Size} */
  size?: Pagination.Size
  /** @see {@link Pagination.Guards} */
  guards?: Pagination.Guards
}
export namespace Pagination {
  /** Where the value that continues pagination is read from. */
  export type From = From.Body | From.Count
  export namespace From {
    /** Reads the continuation value from within the response body. */
    export interface Body {
      type: 'body'
      /**
       * Dot-separated path to the value.
       *
       * @example 'nextPageToken'
       * @example 'meta.paging.cursor'
       */
      path: string
    }
    export namespace Body {
      export const isBody: TypeChecker<Body> = TypeChecker.Factory.object<Body>(
        {
          path: TypeChecker.isString,
          type: TypeChecker.Factory.exact('body'),
        },
      ).usingAlias('integrations.Pagination.From.Body')
    }

    /**
     * Counts the continuation value locally, for endpoints that report no
     * cursor of their own.
     */
    export interface Count {
      type: 'count'
      /** Jira counts records consumed; GitHub counts pages requested. */
      unit: 'records' | 'pages'
      /**
       * Value sent on the first request.
       *
       * @default 0 when counting records, 1 when counting pages
       */
      first?: number
      /**
       * Dot-separated path to the total record count, used to stop before
       * requesting a page that is known to be empty.
       *
       * @example 'total'
       */
      totalPath?: string
    }
    export namespace Count {
      export const isCount: TypeChecker<Count> =
        TypeChecker.Factory.object<Count>({
          first: TypeChecker.Factory.optional(TypeChecker.isNumber),
          totalPath: TypeChecker.Factory.optional(TypeChecker.isString),
          type: TypeChecker.Factory.exact('count'),
          unit: TypeChecker.Factory.union(
            TypeChecker.Factory.exact('records'),
            TypeChecker.Factory.exact('pages'),
          ),
        }).usingAlias('integrations.Pagination.From.Count')

      /** Value sent on the first request when the source does not set one. */
      export function getFirst(from: Readonly<Count>): number {
        return from.first ?? (from.unit === 'records' ? 0 : 1)
      }
    }

    export const isFrom: TypeChecker<From> = TypeChecker.Factory.union(
      Body.isBody,
      Count.isCount,
    ).usingAlias('integrations.Pagination.From')
  }

  /** Page size requested from the endpoint. */
  export interface Size {
    /**
     * Query parameter carrying the page size.
     *
     * @example 'maxResults'
     * @example 'per_page'
     */
    parameter: string
    /** Records requested per page, ideally the endpoint's maximum. */
    value: number
  }
  export namespace Size {
    export const isSize: TypeChecker<Size> = TypeChecker.Factory.object<Size>({
      parameter: TypeChecker.isString,
      value: TypeChecker.isNumber,
    }).usingAlias('integrations.Pagination.Size')
  }

  /**
   * Bounds on a single source's pagination. These exist to stop a
   * misconfigured source rather than to limit how much data is imported: an
   * import always requests every page its endpoint offers, and a bound being
   * reached fails the import instead of quietly returning part of it.
   */
  export interface Guards {
    /**
     * Requests made for one source before the import fails.
     *
     * @default {@link Guards.DEFAULT_MAX_REQUESTS}
     */
    maxRequests?: number
    /**
     * Wall-clock budget for one source, in milliseconds, kept below the
     * runtime's own timeout so that exhausting it fails legibly rather than
     * being killed part-way through.
     *
     * @default {@link Guards.DEFAULT_TIME_BUDGET_MS}
     */
    timeBudgetMs?: number
  }
  export namespace Guards {
    /** @see {@link Guards.maxRequests} */
    export const DEFAULT_MAX_REQUESTS = 1000

    /** @see {@link Guards.timeBudgetMs} */
    export const DEFAULT_TIME_BUDGET_MS = 600_000

    export const isGuards: TypeChecker<Guards> =
      TypeChecker.Factory.object<Guards>({
        maxRequests: TypeChecker.Factory.optional(TypeChecker.isNumber),
        timeBudgetMs: TypeChecker.Factory.optional(TypeChecker.isNumber),
      }).usingAlias('integrations.Pagination.Guards')

    /**
     * Throws when a source has used more than it is allowed to.
     *
     * Call this only once another request is known to be needed, so that a
     * source which finishes exactly as a bound is reached finishes cleanly
     * rather than failing.
     *
     * @throws {@link errors.FailedOperationError}
     */
    export function assertWithin(
      guards: Readonly<Guards> | undefined,
      progress: Readonly<Progress>,
    ): void {
      const maxRequests = guards?.maxRequests ?? DEFAULT_MAX_REQUESTS
      if (progress.requests >= maxRequests) {
        throw exhausted(
          `Pagination stopped after ${progress.requests} requests without reaching the end of the data.`,
        )
      }

      const timeBudgetMs = guards?.timeBudgetMs ?? DEFAULT_TIME_BUDGET_MS
      if (progress.elapsedMs >= timeBudgetMs) {
        throw exhausted(
          `Pagination stopped after ${Math.round(
            progress.elapsedMs / 1000,
          )}s without reaching the end of the data.`,
        )
      }
    }

    /** Creates the error for a source that could not be read to its end. */
    function exhausted(reason: string): errors.FailedOperationError {
      return new errors.FailedOperationError({
        description: 'import every record from the third-party API',
        reason,
      })
    }
  }

  /** What one request achieved, used to decide whether another is needed. */
  export interface Progress {
    /** Parsed body of the response just received. */
    body: unknown
    /**
     * Records extracted from the response just received, which is how a source
     * whose cursor is a field of its last record finds that record.
     */
    records: readonly Readonly<Record<string, unknown>>[]
    /** Records extracted from every response so far, including this one. */
    receivedTotal: number
    /** Requests made for this source so far, including the one just made. */
    requests: number
    /** Milliseconds since the first request for this source was made. */
    elapsedMs: number
    /** Continuation value used for the request just made, if there was one. */
    cursor?: string
  }

  /**
   * Continuation value for the first request, which is absent for a source
   * that reads its value from a response, because there is no response yet.
   */
  export function first(pagination: Readonly<Pagination>): string | undefined {
    return pagination.from.type === 'count'
      ? String(From.Count.getFirst(pagination.from))
      : undefined
  }

  /**
   * Continuation value for the request after the one just made, or `undefined`
   * once the endpoint has no more records to offer.
   *
   * @see {@link Guards.assertWithin}, which bounds how many times a caller
   * acts on this.
   */
  export function next(
    pagination: Readonly<Pagination>,
    progress: Readonly<Progress>,
  ): string | undefined {
    if (pagination.from.type === 'count') {
      return nextCount(pagination, pagination.from, progress)
    }

    const value = read(progress.body, pagination.from.path)
    // NOTE: A value equal to the one just used would repeat the same request
    // forever, which some endpoints do instead of omitting it on the last page.
    return !TypeChecker.isString(value) ||
      value === '' ||
      value === progress.cursor
      ? undefined
      : value
  }

  export const isPagination: TypeChecker<Pagination> =
    TypeChecker.Factory.object<Pagination>({
      from: From.isFrom,
      guards: TypeChecker.Factory.optional(Guards.isGuards),
      parameter: TypeChecker.isString,
      size: TypeChecker.Factory.optional(Size.isSize),
    }).usingAlias('integrations.Pagination')

  export const assertIsPagination: TypeAsserter<Pagination> =
    TypeAsserter.Factory.from(isPagination)

  /** Continuation value for a source that counts its own position. */
  function nextCount(
    pagination: Readonly<Pagination>,
    from: Readonly<From.Count>,
    progress: Readonly<Progress>,
  ): string | undefined {
    const received = progress.records.length
    if (received === 0) {
      return undefined
    }
    // A page smaller than the one requested is the last one, which saves the
    // request for the empty page that would otherwise prove it.
    if (pagination.size && received < pagination.size.value) {
      return undefined
    }
    if (from.totalPath !== undefined) {
      const total = read(progress.body, from.totalPath)
      if (TypeChecker.isNumber(total) && progress.receivedTotal >= total) {
        return undefined
      }
    }

    const advanced =
      from.unit === 'records' ? progress.receivedTotal : progress.requests
    return String(From.Count.getFirst(from) + advanced)
  }

  /** Reads the value at a dot-separated path within a response body. */
  function read(body: unknown, path: string): unknown {
    let value = body
    for (const property of path.split('.')) {
      if (!TypeChecker.isObject(value)) {
        return undefined
      }
      value = (value as Readonly<Record<string, unknown>>)[property]
    }
    return value
  }
}
